import type { OrderPen, OrderPenCreate } from '@farmfree/common/types/order/orderPen'
import type { OrderPenWithCommerce } from '@farmfree/common/types/order/orderPenWithCommerce'
import type { ApiResponse } from '@farmfree/common/types/apiResponse'
import { generateRandomIv } from '@/utils/crypto/generateRandomIv'
import { encryptWithIv } from '@farmfree/common/utils/crypto/encryption'
import { decryptWithIv } from '@farmfree/common/utils/crypto/decryption'
import { getFirebaseDb } from '@/services/firebaseService'
import { getEncryptionKey } from '@/env'
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  writeBatch,
  serverTimestamp,
  increment,
  Timestamp,
  type FieldValue,
} from 'firebase/firestore'

// Firestore 저장용 타입 (serverTimestamp() 사용)
type OrderPenForSave = Omit<Partial<OrderPen>, 'dateCreated' | 'dateModified'> & {
  dateCreated?: FieldValue
  dateModified: FieldValue
}

// OrderPen의 string 타입 필드만 추출하는 유틸리티 타입
type StringFieldsOf<T> = {
  [K in keyof T]: T[K] extends string ? K : never
}[keyof T]

type OrderPenStringKey = StringFieldsOf<OrderPen>

// 암호화할 필드 목록 (타입 안전성 보장)
const ENCRYPTED_FIELDS = [
  'receiverName',
  'receiverPhone1',
  'receiverAddress',
  'senderName',
  'senderPhone1',
  'senderAddress',
  'deliveryNote',
] as const satisfies readonly OrderPenStringKey[]

// 암호화 필드 타입 추출
type EncryptedField = (typeof ENCRYPTED_FIELDS)[number]

// Plain 필드 타입 (필요시 사용)
// type PlainStringField = Exclude<OrderPenStringKey, EncryptedField>

// 함수를 제거하고 객체를 정리하는 헬퍼 함수
function sanitizeValue(value: any): any {
  // 함수는 제외
  if (typeof value === 'function') {
    return undefined
  }

  // null 또는 undefined
  if (value === null || value === undefined) {
    return value
  }

  // 배열
  if (Array.isArray(value)) {
    return value.map(sanitizeValue).filter((v) => v !== undefined)
  }

  // Timestamp 객체 (fake Timestamp 포함)
  if (typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value) {
    return new Timestamp(value.seconds, value.nanoseconds)
  }

  // 일반 객체
  if (typeof value === 'object') {
    const sanitized: any = {}
    for (const key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        const sanitizedValue = sanitizeValue(value[key])
        if (sanitizedValue !== undefined) {
          sanitized[key] = sanitizedValue
        }
      }
    }
    return sanitized
  }

  // 기본 타입 (string, number, boolean)
  return value
}

// OrderPen 데이터 암호화
function encryptOrderPen(orderPen: Partial<OrderPen>, iv: string): Partial<OrderPen> {
  // Vue reactive wrapper 제거 및 함수 필터링
  const encrypted: Partial<OrderPen> = {}

  // 모든 필드를 순회하며 값 정리 (함수 제거)
  for (const key in orderPen) {
    if (Object.prototype.hasOwnProperty.call(orderPen, key)) {
      const value = orderPen[key as keyof OrderPen]
      const sanitized = sanitizeValue(value)

      if (sanitized !== undefined) {
        encrypted[key as keyof OrderPen] = sanitized as any
      }
    }
  }

  // 암호화 필드 처리
  ENCRYPTED_FIELDS.forEach((field) => {
    if (encrypted[field as keyof OrderPen]) {
      const value = encrypted[field as keyof OrderPen]
      if (typeof value === 'string') {
        encrypted[field as keyof OrderPen] = encryptWithIv(value, iv, getEncryptionKey()) as any
      }
    }
  })

  return encrypted
}

// OrderPen 데이터 복호화
function decryptOrderPen(orderPen: any): OrderPen {
  // IV가 없으면 그대로 반환 (레거시 데이터)
  if (!orderPen.iv) {
    return orderPen as OrderPen
  }

  const decrypted = { ...orderPen }
  const iv = orderPen.iv

  ENCRYPTED_FIELDS.forEach((field) => {
    if (decrypted[field]) {
      try {
        decrypted[field] = decryptWithIv(decrypted[field], iv, getEncryptionKey())
      } catch (error) {
        console.error(`복호화 실패 - ${field}:`, error)
        // 복호화 실패 시 원본 유지
      }
    }
  })

  return decrypted as OrderPen
}

export function createActiveOrderEncryptedService(role: 'admin' | 'guest') {
  return {
    // 수정된 항목 조회 (since 이후) - 복호화 적용
    async getModified(
      companyId: string,
      since: number
    ): Promise<ApiResponse<OrderPenWithCommerce[]>> {
      try {
        const db = getFirebaseDb()
        const collectionRef = collection(db, 'v2_companies', companyId, 'v2_orderPens')
        const q = query(
          collectionRef,
          where('dateModified', '>', Timestamp.fromMillis(since)),
          orderBy('dateModified', 'asc')
        )

        const snapshot = await getDocs(q)
        const orders = snapshot.docs.map((doc) => {
          const data = { ...doc.data(), id: doc.id }
          return decryptOrderPen(data) as OrderPenWithCommerce
        })

        return {
          isSuccess: true,
          data: orders,
          message: 'Success',
        }
      } catch (error) {
        console.error('OrderPen 조회 실패:', error)
        return {
          isSuccess: false,
          data: [],
          message: error instanceof Error ? error.message : 'OrderPen 조회 실패',
        }
      }
    },

    // 삭제된 ID 목록 조회 (복호화 불필요)
    async getDeleted(companyId: string): Promise<ApiResponse<string[]>> {
      try {
        const db = getFirebaseDb()
        // 삭제된 문서를 추적하는 별도 컬렉션 조회
        const deletedRef = collection(db, 'v2_companies', companyId, 'v2_deletedOrderPens')
        const snapshot = await getDocs(deletedRef)
        const deletedIds = snapshot.docs.map((doc) => doc.id)

        return {
          isSuccess: true,
          data: deletedIds,
          message: 'Success',
        }
      } catch (error) {
        console.error('삭제된 OrderPen ID 조회 실패:', error)
        return {
          isSuccess: false,
          data: [],
          message: error instanceof Error ? error.message : '삭제된 ID 조회 실패',
        }
      }
    },

    // 단일 항목 저장 - 암호화 적용 (생성/수정 통합)
    async saveItem(
      companyId: string,
      item: Partial<OrderPenWithCommerce>
    ): Promise<ApiResponse<{ id: string }>> {
      try {
        const db = getFirebaseDb()

        // 생성 vs 수정 판단
        const isNewItem = !item.id || item.id === ''

        if (isNewItem) {
          // ========== 생성 로직 ==========
          // 새 항목 ID 생성
          const newId = `order_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`

          // IV 생성
          const iv = generateRandomIv()
          const encrypted = encryptOrderPen(item, iv)

          // payload 구성 (dateCreated, dateModified는 serverTimestamp()로 자동 설정)
          const payload: OrderPenForSave = {
            ...encrypted,
            iv,
            dateCreated: serverTimestamp(),
            dateModified: serverTimestamp(),
          }

          const docRef = doc(db, 'v2_companies', companyId, 'v2_orderPens', newId)
          await setDoc(docRef, payload)

          // Company의 orderPenVersion 업데이트
          const companyRef = doc(db, 'v2_companies', companyId)
          await setDoc(
            companyRef,
            {
              versionInfo: {
                orderPenVersion: increment(0.0001),
              },
            },
            { merge: true }
          )

          return {
            isSuccess: true,
            data: { id: newId },
            message: 'Success',
          }
        } else {
          // ========== 수정 로직 ==========
          // 타입 가드: item.id가 반드시 존재 (isNewItem 체크로 보장됨)
          const itemId: string = item.id!

          // 기존 IV 사용 또는 새로 생성
          const iv = item.iv || generateRandomIv()
          const encrypted = encryptOrderPen(item, iv)

          // dateCreated, dateModified 제외하고 payload 구성
          const { dateCreated, dateModified, ...rest } = encrypted
          const payload: OrderPenForSave = {
            ...rest,
            iv,
            dateModified: serverTimestamp(),
          }

          const docRef = doc(db, 'v2_companies', companyId, 'v2_orderPens', itemId)
          await setDoc(docRef, payload, { merge: true })

          // Company의 orderPenVersion 업데이트
          const companyRef = doc(db, 'v2_companies', companyId)
          await setDoc(
            companyRef,
            {
              versionInfo: {
                orderPenVersion: increment(0.0001),
              },
            },
            { merge: true }
          )

          return {
            isSuccess: true,
            data: { id: itemId },
            message: 'Success',
          }
        }
      } catch (error) {
        console.error('OrderPen 저장 실패:', error)
        return {
          isSuccess: false,
          message: error instanceof Error ? error.message : 'OrderPen 저장 실패',
        }
      }
    },

    // 여러 항목 일괄 업데이트 - 암호화 적용
    async saveItems(
      companyId: string,
      items: (Partial<OrderPenWithCommerce> & { id: string })[]
    ): Promise<ApiResponse> {
      try {
        const db = getFirebaseDb()
        const batch = writeBatch(db)

        // 각 항목별로 암호화 및 업데이트
        items.forEach((item) => {
          const iv = item.iv || generateRandomIv()
          const encrypted = encryptOrderPen(item as Partial<OrderPen>, iv)

          // dateCreated, dateModified 제외하고 payload 구성
          const { dateCreated, dateModified, ...rest } = encrypted
          const payload: OrderPenForSave = {
            ...rest,
            iv,
            dateModified: serverTimestamp(),
          }

          const docRef = doc(db, 'v2_companies', companyId, 'v2_orderPens', item.id)
          batch.set(docRef, payload, { merge: true })
        })

        // Company의 orderPenVersion 업데이트
        const companyRef = doc(db, 'v2_companies', companyId)
        batch.set(
          companyRef,
          {
            versionInfo: {
              orderPenVersion: increment(0.0001),
            },
          },
          { merge: true }
        )

        await batch.commit()

        return {
          isSuccess: true,
          message: 'Success',
        }
      } catch (error) {
        console.error('OrderPen 일괄 저장 실패:', error)
        return {
          isSuccess: false,
          message: error instanceof Error ? error.message : 'OrderPen 일괄 저장 실패',
        }
      }
    },

    // 단일 항목 삭제 (암호화 무관)
    async deleteItem(companyId: string, id: string): Promise<ApiResponse> {
      try {
        const db = getFirebaseDb()
        const batch = writeBatch(db)

        // 원본 문서 삭제
        const docRef = doc(db, 'v2_companies', companyId, 'v2_orderPens', id)
        batch.delete(docRef)

        // 삭제된 문서 추적용 문서 생성
        const deletedRef = doc(db, 'v2_companies', companyId, 'v2_deletedOrderPens', id)
        batch.set(deletedRef, {
          deletedAt: serverTimestamp(),
        })

        // Company의 orderPenVersion 업데이트
        const companyRef = doc(db, 'v2_companies', companyId)
        batch.set(
          companyRef,
          {
            versionInfo: {
              orderPenVersion: increment(0.0001),
            },
          },
          { merge: true }
        )

        await batch.commit()

        return {
          isSuccess: true,
          message: 'Success',
        }
      } catch (error) {
        console.error('OrderPen 삭제 실패:', error)
        return {
          isSuccess: false,
          message: error instanceof Error ? error.message : 'OrderPen 삭제 실패',
        }
      }
    },
  }
}
